import gql from "graphql-tag";
import {useCallback} from "react";

import {useMutation} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import type {StopResponseType} from "./__graphql-types__/index.ts";

export const STOP_RESPONSE_MUTATION: GqlOperation<
    StopResponseType["response"],
    StopResponseType["variables"]
> = gqlOp(gql`
    mutation StopResponse($input: StopResponseInput!) {
        stopResponse(input: $input) {
            error {
                code
            }
        }
    }
`);

/**
 * Hook to produce a function for stopping guide responses.
 *
 * This hook returns a function that can be invoked to stop a request
 * to the AI Guide.
 *
 * @returns A function that takes an identifier for the guide
 * conversation to be stopped. When invoked, this function sends
 * a request to stop the given conversation, and returns a promise
 * that resolves when the request is complete.
 */
export const useStopResponseMutation = (): ((
    stopId: string,
) => Promise<void>) => {
    const [mutate] = useMutation(STOP_RESPONSE_MUTATION);
    const stopResponseMutation = useCallback(
        async (stopId: string) => {
            const result = await mutate({input: {stopId}});
            // If we have an error status or we got a successful response but
            // it has an error in it, then we throw an error.
            // We should never see the loading status here since this waits
            // for the mutation to be complete, and we the aborted status is
            // something we can ignore for this case.
            const error =
                result.status === "error"
                    ? result.error
                    : result.status === "success" &&
                        result.data.stopResponse.error
                      ? result.data.stopResponse.error.code
                      : null;
            if (error) {
                throw new KAError(
                    "Failed to record AI Guide stop response mutation",
                    Errors.Internal,
                    {cause: error},
                );
            }
        },
        [mutate],
    );
    return stopResponseMutation;
};
